import { create } from "zustand";
import { FileMetadata } from "@domain/schemas/BI";
import { State } from "@progress/kendo-data-query";
import { runtimeConfig } from "@utils/runtimeConfig/runtimeConfig";
import { buildRootBreadcrumbLabel } from "@presentation/stores/FileExplorerStore";

export type FastaExplorerItem = {
  id: string;
  fileName: string;
  isFolder: boolean;
  sizeInBytes: number;
  fileCount?: number | null;
  sourcePath?: string | null;
  /**
   * Collision-safe row identity (`sourcePath::id` when ambiguous, else just `id`) - see
   * `FastaExplorerGrid.tsx`'s `buildRowKey`.
   */
  rowKey: string;
};

export type FastaSelectedState = {
  [id: string]: FileMetadata & { isFolder: boolean };
};

interface FastaExplorerStore {
  fastaItems: FastaExplorerItem[];
  metaState: State;
  selectedState: FastaSelectedState;
  breadcrumbItems: { id: string; text: string; disabled: boolean; sourcePath?: string | null }[];
  /**
  The project this store's `breadcrumbItems`/`selectedState` currently belong to - kept in the store
  (not a component-local ref) because Kendo's `TabStripTab` unmounts an inactive tab's content, which
  would otherwise reset a local "last seen project" ref and make a genuine project switch that happened
  while this tab was hidden look like a fresh first load, leaving the previous project's breadcrumb/scope
  in place instead of resetting it.
  */
  lastProjectId: string | null;
  setLastProjectId: (projectId: string) => void;
  resetSelectedGridData: () => void;
  setFastaItems: (items: FastaExplorerItem[]) => void;
  setSelectedState: (newSelectedState: FastaSelectedState) => void;
  setBreadcrumbItems: (items: { id: string; text: string; disabled: boolean; sourcePath?: string | null }[]) => void;
  setMetaState: (state: State) => void;
}

export const useFastaExplorerStore = create<FastaExplorerStore>((set) => ({
  fastaItems: [],
  metaState: {
    skip: 0,
    take: 50,
    sort: [{ field: "fileName", dir: "asc" }],
    filter: undefined,
  },
  selectedState: {},
  breadcrumbItems: [{ id: "home", text: buildRootBreadcrumbLabel(runtimeConfig.FASTA_CONTAINER_NAME), disabled: false }],
  lastProjectId: null,
  setLastProjectId: (projectId) => set({ lastProjectId: projectId }),
  setBreadcrumbItems: (items) => set({ breadcrumbItems: items }),
  setFastaItems: (items) => set({ fastaItems: items }),
  setSelectedState: (newSelectedState) => set({ selectedState: newSelectedState }),
  resetSelectedGridData: () => set({ selectedState: {} }),
  setMetaState: (state) => set({ metaState: state }),
}));
